import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CartItem, Product, VariantSelection } from '../types/product';
import { defaultSelection, resolveVariant } from '../utils/products';
interface CartContextValue {
  items: CartItem[];
  count: number;
  subtotal: number;
  isOpen: boolean;
  addItem: (product: Product, selection?: VariantSelection, qty?: number, openDrawer?: boolean) => void;
  setQty: (key: string, qty: number) => void;
  removeItem: (key: string) => void;
  clearCart: () => void;
  openCart: () => void;
  closeCart: () => void;
}
const CartContext = createContext<CartContextValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const addItem = useCallback((product: Product, selection?: VariantSelection, qty = 1, openDrawer = true) => {
    const v = resolveVariant(product, selection ?? defaultSelection(product));
    setItems(prev => {
      const existing = prev.find(i => i.key === v.key);
      if (existing) return prev.map(i => i.key === v.key ? {
        ...i,
        qty: i.qty + qty
      } : i);
      return [...prev, {
        key: v.key,
        product,
        qty,
        unitPrice: v.price,
        image: v.image,
        options: product.variants ? v.labels : []
      }];
    });
    if (openDrawer) setIsOpen(true);
  }, []);
  const setQty = useCallback((key: string, qty: number) => {
    setItems(prev => qty <= 0 ? prev.filter(i => i.key !== key) : prev.map(i => i.key === key ? {
      ...i,
      qty
    } : i));
  }, []);
  const removeItem = useCallback((key: string) => {
    setItems(prev => prev.filter(i => i.key !== key));
  }, []);
  const value = useMemo<CartContextValue>(() => ({
    items,
    count: items.reduce((s, i) => s + i.qty, 0),
    subtotal: Math.round(items.reduce((s, i) => s + i.qty * i.unitPrice, 0) * 100) / 100,
    isOpen,
    addItem,
    setQty,
    removeItem,
    clearCart: () => setItems([]),
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false)
  }), [items, isOpen, addItem, setQty, removeItem]);
  return <CartContext.Provider value={value} data-id="element-602" data-mp="3od5bn5ydz0f3">{children}</CartContext.Provider>;
}
export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}